import { createSlice } from "@reduxjs/toolkit";
import {
  addOrRemoveMultipleElements,
  deleteWall,
  generateGameLayer,
  generateGrid,
  modifyGridSize,
  moveObject,
  multiGridGeneration,
  placeItemsInGrid,
  removeLayoutFromParentedList,
  removeParentLayoutFromTile,
  removeSpawn,
  resetLayers,
  updateLastMainVideoArray,
  updateMySpawnWalls,
  updateObject,
  updateObjectOverview,
  updateObjectOverviewOnGameLayer,
} from "./utils";
import { gamesTypes } from "../../components/configurator/utils/utils";

function initializeConfiguratorState() {
  return {
    loading: false,
    backdrop: false,
    grid: generateGrid(11, 11),
    selectedTile: null,
    rowNumber: 11,
    columnNumber: 11,
    parentLayout: "default",
    parentLayoutsList: [{ label: "default", value: "default", key: "default" }],
    layout: "normal", // normal, event, theme
    parentedLayoutsList: { default: [] },
    layouts: [],
    is3dView: false,
    areWallsHidden: false,
    areEventsHidden: false,
    areThemesHidden: false,
    isUserAdding: false,
    isUserDeleting: false,
    previousTiles: [],
    gridsKeysToDisplay: [],
    multiSelection: {
      active: false,
      isDelete: false,
      move: false,
      associateCoords: false, // if true alors calculer aussi le x-y inversé (arc de triomphe)
      unlimited: false, // for objects (santa) on multiple cases without limits
      x: 0, // -1 for infinite
      y: 0, // -1 for infinite
      tile: null, // use for custom frame size (if unlimited true => array else => object)
      data: null, // data to add to tile
      oldLayer: null, // old layer for moving object
      layer: null, // destination layer for moving object
    },
    needSave: false, // if true, display the confirmation modal 'leave without saving'
    objectsOverview: [],
    modalManager: {
      type: null, // backdrop Or dialog
      renderer: null, // data inside the form
      open: false,
      title: "",
      saveTrigger: false,
      disableSave: true,
    },
    confirmation: {
      open: false,
      agree: null,
      element: null,
    },
    lastMainVideo: [],
    multiUpload: {
      active: false,
      files: [],
      activeFile: null,
      currentTile: null,
      orientation: null,
    },
    wallsManagement: {
      orientation: "",
      color: { r: 0, g: 0, b: 0, a: 1 },
      mode: "add",
    },
  };
}

export const configuratorSlice = createSlice({
  name: "configurator",
  initialState: initializeConfiguratorState(),
  reducers: {
    updateGridKeysToDisplay: (state, action) => {
      state.gridsKeysToDisplay = action.payload;
    },
    generateMyGameLayer: (state, action) => {
      state.grid = generateGameLayer(
        state.grid,
        action.payload.gameName,
        action.payload.row,
        action.payload.column
      );
      state.layouts = [
        ...state.layouts,
        {
          name: action.payload.gameName,
          row: action.payload.row,
          column: action.payload.column,
        },
      ];
      state.parentLayoutsList = [
        ...state.parentLayoutsList,
        {
          label: action.payload.gameName,
          value: action.payload.gameName,
          key: action.payload.gameName,
        },
      ];
    },
    removeParentLayoutFromLayer: (state, action) => {
      state.needSave = true;
      state.grid = removeParentLayoutFromTile(state.grid, action.payload);
    },
    updateParentedLayoutsList: (state, action) => {
      state.needSave = true;
      state.parentedLayoutsList = action.payload;
    },
    addCustomLayout: (state, action) => {
      state.grid = {
        ...state.grid,
        ...generateGrid(
          state.grid.default.length,
          state.grid.default[0].length,
          action.payload.key
        ),
      };
      state.layouts = [
        ...state.layouts,
        {
          name: action.payload.key,
          row: state.grid.default.length,
          column: state.grid.default[0].length,
        },
      ];
      state.parentLayout = action.payload.key;
      state.parentLayoutsList = [...state.parentLayoutsList, action.payload];
      state.layout = "normal";
    },
    removeCustomLayout: (state, action) => {
      state.needSave = true;
      const result = removeLayoutFromParentedList(
        state.grid,
        state.parentedLayoutsList,
        state.parentLayoutsList,
        action.payload
      );
      state.layouts = state.layouts.filter((l) => l.name !== action.payload);
      state.parentedLayoutsList = result.parentedList;
      state.grid = result.grid;
      state.parentLayoutsList = result.parentLayoutsList;
      state.parentLayout = "default";
      state.layout = "normal";
    },
    updatePreviousTiles: (state, action) => {
      state.previousTiles = action.payload;
    },
    updateIsUserAdding: (state, action) => {
      if (action.payload === false) state.isUserAdding = action.payload;
      else state.isUserAdding = true;
    },
    updateIsUserDeleting: (state, action) => {
      if (action.payload === false) state.isUserDeleting = action.payload;
      else state.isUserDeleting = true;
    },
    updateWallsVisibility: (state, action) => {
      state.selectedTile = null;
      if (action.payload === false) state.areWallsHidden = action.payload;
      else state.areWallsHidden = !state.areWallsHidden;
    },
    updateEventsVisibility: (state, action) => {
      state.selectedTile = null;
      if (action.payload === false) state.areEventsHidden = action.payload;
      else state.areEventsHidden = !state.areEventsHidden;
    },
    updateThemesVisibility: (state, action) => {
      state.selectedTile = null;
      if (action.payload === false) state.areThemesHidden = action.payload;
      else state.areThemesHidden = !state.areThemesHidden;
    },
    toggleBackdrop: (state) => {
      state.backdrop = !state.backdrop;
    },
    update3dView: (state, action) => {
      state.selectedTile = null;
      if (action.payload === false) state.is3dView = action.payload;
      else state.is3dView = !state.is3dView;
    },
    updateGridSize: (state, action) => {
      state.selectedTile = null;
      const tmp = modifyGridSize(state.grid, action.payload, state.layouts);
      state.objectsOverview = updateObjectOverviewOnGameLayer(
        state.objectsOverview,
        action.payload.parentLayout,
        action.payload
      );
      state.grid = tmp.grid;
      if (tmp.mainVideoDeleted) state.lastMainVideo = [];
      state[tmp.lineChange.key] = tmp.lineChange.value;
      state.layouts = tmp.layouts;
      state.needSave = true;
    },
    reinitializeConfig: (state) => {
      state.grid = generateGrid(state.rowNumber, state.columnNumber);
      state.multiSelection = resetLayers.multiSelection;
      state.modalManager = resetLayers.modalManager;
      state.multiSelection = resetLayers.multiSelection;
      state.layout = "normal";
      state.selectedTile = null;
      state.needSave = false;
      state.lastMainVideo = [];
      state.objectsOverview = [];
      state.parentLayout = "default";
      state.parentLayoutsList = [
        { label: "default", value: "default", key: "default" },
      ];
      state.parentedLayoutsList = { default: [] };
      state.gridsKeysToDisplay = [];
    },
    setItemsInGrid: (state, action) => {
      if (!gamesTypes.includes(state.parentLayout)) {
        state.rowNumber = action.payload.rowNumber;
        state.columnNumber = action.payload.columnNumber;
      }
      state.layouts = action.payload.layouts;
      const result = multiGridGeneration(
        action.payload.rowNumber,
        action.payload.columnNumber,
        action.payload.layouts,
        [
          ...action.payload.objects,
          ...action.payload.themes,
          ...action.payload.walls,
        ]
      );
      state.grid = result.grid;
      state.parentedLayoutsList = result.parentedLayoutsList;
      state.parentLayoutsList = result.parentLayoutsList;
      const tmp = placeItemsInGrid(state.grid, action.payload, {
        rowNumber: state.rowNumber,
        columnNumber: state.columnNumber,
      });
      state.grid = tmp.grid;
      state.objectsOverview = updateObjectOverview(state.grid);
      if (!state.grid.hasOwnProperty(state.parentLayout)) {
        state.parentLayout = "default";
        state.rowNumber = state.grid.default.length;
        state.columnNumber = state.grid.default[0].length;
      }
      if (tmp.mainVideo) {
        state.lastMainVideo = tmp.mainVideo.map((item) => {
          const { startTile, endTile, ...left } = item.data;
          return {
            layout: left.layout,
            data: left,
            startTile: item.tile,
            endTile: item.tileEnd,
          };
        });
      }
    },

    updateMultiUpload: (state, action) => {
      state.multiUpload = { ...state.multiUpload, ...action.payload };
    },
    applyPredefinedFormat: (state, action) => {
      state.grid = generateGrid(
        state.rowNumber,
        state.columnNumber,
        "default",
        action.payload
      );
      state.needSave = true;
    },
    updateLastMainVideo: (state, action) => {
      state.lastMainVideo = updateLastMainVideoArray(
        state.lastMainVideo,
        state.parentedLayoutsList,
        action.payload
      );
    },
    findAndRemoveOldSpawn: (state) => {
      state.grid = removeSpawn(state.grid);
    },
    updateWallsManagment: (state, action) => {
      state.wallsManagement = action.payload;
    },
    updateConfirmation: (state, action) => {
      state.confirmation = { ...action.payload };
    },
    updateModalManager: (state, action) => {
      state.modalManager = { ...state.modalManager, ...action.payload };
    },
    updateMultiSelection: (state, action) => {
      state.multiSelection = { ...state.multiSelection, ...action.payload };
    },
    updateSelectedTile: (state, action) => {
      state.selectedTile = action.payload;
    },
    updateLayout: (state, action) => {
      state.layout = action.payload;
    },
    updateParentLayout: (state, action) => {
      state.parentLayout = action.payload;
      state.rowNumber = state.grid[action.payload].length;
      state.columnNumber = state.grid[action.payload][0].length;
    },
    addMultipleElements: (state, action) => {
      state.grid = addOrRemoveMultipleElements(
        state.grid,
        state.parentLayout,
        action.payload
      );
      state.objectsOverview = updateObjectOverview(state.grid);
      state.needSave = true;
    },
    removeMultipleElements: (state, action) => {
      state.grid = addOrRemoveMultipleElements(
        state.grid,
        state.parentLayout,
        action.payload
      );
      state.objectsOverview = updateObjectOverview(state.grid);
      state.needSave = true;
    },
    addElement: (state, action) => {
      state.grid = updateObject(state.grid, state.parentLayout, action.payload);
      state.objectsOverview = updateObjectOverview(state.grid);
      state.needSave = true;
    },
    removeElement: (state, action) => {
      state.grid = updateObject(state.grid, state.parentLayout, action.payload);
      state.objectsOverview = updateObjectOverview(state.grid);
      state.needSave = true;
    },
    moveElement: (state, action) => {
      state.grid = moveObject(state.grid, action.payload, state.parentLayout);
      state.objectsOverview = updateObjectOverview(state.grid);
      state.needSave = true;
    },
    updateElement: (state, action) => {
      const { noSaveTrigger, ...left } = action.payload;
      const layoutToUpdate = action.payload.hasOwnProperty("screenOldLayout")
        ? action.payload.screenOldLayout
        : state.parentLayout;
      state.grid = updateObject(state.grid, layoutToUpdate, left);
      state.objectsOverview = updateObjectOverview(state.grid);
      if (!noSaveTrigger) state.needSave = true;
    },
    updateWalls: (state, action) => {
      const { noSaveTrigger, ...left } = action.payload;
      if (state.wallsManagement.mode === "add")
        state.grid = updateObject(state.grid, state.parentLayout, left);
      else
        state.grid = deleteWall(state.grid, state.parentLayout, action.payload);
      if (!noSaveTrigger) state.needSave = true;
    },
    updateSpawnWalls: (state, action) => {
      state.grid = updateMySpawnWalls(state.grid, action.payload, {
        rowNumber: state.rowNumber,
        columnNumber: state.columnNumber,
      });
    },
  },
});

export const {
  addCustomLayout,
  removeCustomLayout,
  addElement,
  removeElement,
  updateElement,
  updateWalls,
  addMultipleElements,
  updateWallsManagment,
  updateSelectedTile,
  updateMultiSelection,
  removeMultipleElements,
  updateLayout,
  updateParentLayout,
  moveElement,
  updateModalManager,
  updateConfirmation,
  findAndRemoveOldSpawn,
  updateLastMainVideo,
  applyPredefinedFormat,
  updateMultiUpload,
  setItemsInGrid,
  reinitializeConfig,
  updateGridSize,
  updateSpawnWalls,
  update3dView,
  updateWallsVisibility,
  updateEventsVisibility,
  updateThemesVisibility,
  updateIsUserAdding,
  updateIsUserDeleting,
  updatePreviousTiles,
  toggleBackdrop,
  updateParentedLayoutsList,
  removeParentLayoutFromLayer,
  generateMyGameLayer,
  updateGridKeysToDisplay,
} = configuratorSlice.actions;

export default configuratorSlice.reducer;
